// DashboardSidebar
// ----------------
// Left navigation rail. Renders collapsible groups (Overview, Property, …)
// from menuConfig's ALL_MENU_ITEMS, filtered by role and feature-gate rules.
//
// The component is presentation-only: group expand/collapse state, the
// currently selected page, and the role/feature-gate filters are passed in
// from the parent dashboard.
import React from 'react';
import { Layout, Typography } from 'antd';

import type {
  DashboardComponent,
  MenuGroup,
  MenuGroupKey,
  MenuItem,
} from './menuConfig';

const { Sider } = Layout;
const { Text } = Typography;

export interface DashboardSidebarProps {
  collapsed: boolean;

  currentCondoName: string | undefined;
  summary: { totalOwners: number; feeCollectionRate: number };

  menuGroups: MenuGroup[];
  groupColors: Record<MenuGroupKey, string>;
  filteredItems: MenuItem[];

  expandedGroups: Set<string>;
  onToggleGroup: (groupKey: MenuGroupKey) => void;

  selected: DashboardComponent;
  onSelect: (key: DashboardComponent) => void;
}

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  collapsed,
  currentCondoName, summary,
  menuGroups, groupColors, filteredItems,
  expandedGroups, onToggleGroup,
  selected, onSelect,
}) => {
  return (
    <Sider
      width={260}
      collapsed={collapsed}
      style={{
        background: 'linear-gradient(180deg, #1a2640 0%, #1e2d42 100%)',
        boxShadow: '2px 0 24px rgba(0,0,0,0.25)',
        borderRight: 'none',
        overflow: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Active property summary */}
      <div style={{
        padding: collapsed ? '16px 12px' : '16px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.15)',
      }}>
        {!collapsed && (
          <>
            <Text style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'block',
              marginBottom: 8,
            }}>
              Active Property
            </Text>
            <div style={{
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.07)',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                {currentCondoName || 'Select Property'}
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, display: 'block', marginTop: 2 }}>
                {currentCondoName
                  ? `${summary.totalOwners} Units • ${summary.feeCollectionRate}% Collection`
                  : 'No property selected'}
              </Text>
            </div>
          </>
        )}
      </div>

      <div style={{ height: 'calc(100% - 90px)', overflowY: 'auto', padding: '8px 0' }}>
        {menuGroups.map(group => {
          const groupItems = filteredItems.filter(item => item.group === group.key);
          if (groupItems.length === 0) return null;
          const color = groupColors[group.key] || '#718096';
          const isExpanded = expandedGroups.has(group.key);
          const hasActiveItem = groupItems.some(i => i.key === selected);

          return (
            <div key={group.key} style={{ marginBottom: 2 }}>
              {/* Group header (click to expand/collapse) */}
              {!collapsed ? (
                <div
                  onClick={() => onToggleGroup(group.key)}
                  style={{
                    padding: '10px 16px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <div style={{
                    width: 3, height: 12,
                    background: hasActiveItem ? color : 'rgba(255,255,255,0.2)',
                    borderRadius: 2, flexShrink: 0,
                    transition: 'background 0.2s',
                  }} />
                  <Text style={{
                    fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.8px',
                    color: hasActiveItem ? color : 'rgba(255,255,255,0.35)',
                    flex: 1, transition: 'color 0.2s',
                  }}>
                    {group.label}
                  </Text>
                  <span style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.25)',
                    transition: 'transform 0.2s',
                    display: 'inline-block',
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}>
                    {'\u25BE'}
                  </span>
                </div>
              ) : (
                <div
                  onClick={() => onToggleGroup(group.key)}
                  style={{ margin: '6px 12px 2px', height: 1, background: 'rgba(255,255,255,0.1)', cursor: 'pointer' }}
                />
              )}

              {/* Items (animated show/hide) */}
              <div style={{
                overflow: 'hidden',
                maxHeight: isExpanded ? `${groupItems.length * 48}px` : '0px',
                transition: 'max-height 0.25s ease',
              }}>
                {groupItems.map(item => {
                  const isSelected = selected === item.key;
                  return (
                    <div
                      key={item.key}
                      onClick={() => onSelect(item.key)}
                      title={collapsed ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: collapsed ? 0 : 10,
                        padding: collapsed ? '0' : '0 12px 0 16px',
                        margin: '1px 8px',
                        height: 40,
                        borderRadius: 8,
                        cursor: 'pointer',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        background: isSelected ? 'rgba(255,255,255,0.1)' : 'transparent',
                        borderLeft: isSelected ? `3px solid ${color}` : '3px solid transparent',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => {
                        if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.06)';
                      }}
                      onMouseLeave={e => {
                        if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                      }}
                    >
                      <span style={{
                        fontSize: 15,
                        color: isSelected ? color : 'rgba(255,255,255,0.55)',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        {item.icon}
                      </span>
                      {!collapsed && (
                        <Text style={{
                          fontSize: 13,
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {item.label}
                        </Text>
                      )}
                      {isSelected && !collapsed && (
                        <div style={{
                          marginLeft: 'auto',
                          width: 6, height: 6,
                          borderRadius: '50%',
                          background: color,
                          flexShrink: 0,
                          boxShadow: `0 0 6px ${color}`,
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Sider>
  );
};
